/**
 * Full Window Control for Leaflet Maps
 * Toggles map between normal layout and full browser window
 */
export class FullWindowControl {
	constructor(map, mapElement, mapContainer, options = {}) {
		this.map = map;
		this.mapElement = mapElement; // The Leaflet map canvas element (#geodir_map)
		this.mapContainer = mapContainer; // The wrapper container (.geodir_map_container)
		this.isFullWindow = false;
		this.originalStyles = {};
		this.options = options;
	}

	init() {
		// Check if control should be hidden
		if (this.options.hide) {
			return;
		}

		// Find the zoom control container inside the Leaflet map element
		const zoomControl = this.mapElement.querySelector('.leaflet-control-zoom');

		if (!zoomControl) {
			return;
		}

		// Create full window button
		const button = document.createElement('a');
		button.className = 'leaflet-control-fullwindow';
		button.href = '#';
		button.title = 'Toggle Full Window';
		button.setAttribute('role', 'button');
		button.setAttribute('aria-label', 'Toggle Full Window');

		// Add inline SVG icon (expand)
		button.innerHTML = '<svg width="30" height="30" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg"><path d="M5 10.3V5.9c0-.5.4-.9.9-.9h4.4c.2 0 .4.2.4.4V7c0 .2-.2.4-.4.4h-3v3c0 .2-.2.4-.4.4H5.4a.4.4 0 0 1-.4-.4zm10.3-4.9V7c0 .2.2.4.4.4h3v3c0 .2.2.4.4.4h1.5c.2 0 .4-.2.4-.4V5.9c0-.5-.4-.9-.9-.9h-4.4c-.2 0-.4.2-.4.4zm5.3 9.9H19c-.2 0-.4.2-.4.4v3h-3c-.2 0-.4.2-.4.4v1.5c0 .2.2.4.4.4h4.4c.5 0 .9-.4.9-.9v-4.4c0-.2-.2-.4-.4-.4zm-9.9 5.3V19c0-.2-.2-.4-.4-.4h-3v-3c0-.2-.2-.4-.4-.4H5.4c-.2 0-.4.2-.4.4v4.4c0 .5.4.9.9.9h4.4c.2 0 .4-.2.4-.4z" fill="currentColor"/></svg>';

		// Add click handler
		button.addEventListener('click', (e) => {
			e.preventDefault();
			this.toggle();
		});

		// Append to zoom control (after zoom buttons)
		zoomControl.appendChild(button);

		// Store reference
		this.button = button;

	}

	toggle() {
		if (this.isFullWindow) {
			this.exitFullWindow();
		} else {
			this.enterFullWindow();
		}
	}

	enterFullWindow() {
		// Find the filter wrap
		const filterWrap = this.mapContainer.querySelector('.geodir-map-cat-filter-wrap');

		// Store original parent and position in DOM for later restoration
		this.originalParent = this.mapContainer.parentElement;
		this.originalNextSibling = this.mapContainer.nextSibling;

		// Store original styles for map container
		this.originalStyles = {
			mapContainer: {
				position: this.mapContainer.style.position,
				top: this.mapContainer.style.top,
				left: this.mapContainer.style.left,
				right: this.mapContainer.style.right,
				bottom: this.mapContainer.style.bottom,
				width: this.mapContainer.style.width,
				height: this.mapContainer.style.height,
				zIndex: this.mapContainer.style.zIndex
			},
			filterWrap: filterWrap ? {
				display: filterWrap.style.display,
				maxHeight: filterWrap.style.maxHeight
			} : null
		};

		// Move map container to document.body to escape any positioned parents
		// This ensures position:fixed works relative to viewport, not a containing block
		document.body.appendChild(this.mapContainer);

		// Apply full window styles to map container
		this.mapContainer.style.position = 'fixed';
		this.mapContainer.style.top = '0';
		this.mapContainer.style.left = '0';
		this.mapContainer.style.right = '0';
		this.mapContainer.style.bottom = '0';
		this.mapContainer.style.width = '100vw';
		this.mapContainer.style.height = '100vh';
		this.mapContainer.style.zIndex = '50000';

		// Show filter wrap if hidden and set full height
		if (filterWrap) {
			if (filterWrap.style.display === 'none') {
				filterWrap.style.display = '';
			}
			filterWrap.style.maxHeight = '100vh';
		}

		// Add class for styling
		this.mapContainer.classList.add('geodir-map-fullwindow');

		// Update button state
		this.button.classList.add('active');
		this.button.title = 'Exit Full Window';
		this.button.innerHTML = '<svg width="30" height="30" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg"><path d="M20.6 10.7H16a.9.9 0 0 1-.8-.8v-4.5c0-.2.2-.4.4-.4h1.4c.3 0 .5.2.5.4v3h3c.2 0 .4.2.4.5v1.4c0 .2-.2.4-.4.4zm-9.9-.8V5.4c0-.2-.2-.4-.4-.4H8.9c-.3 0-.5.2-.5.4v3h-3c-.2 0-.4.2-.4.5v1.4c0 .2.2.4.4.4H9.9c.4 0 .8-.4.8-.8zm0 10.7V16c0-.4-.4-.8-.8-.8H5.4c-.2 0-.4.2-.4.4v1.4c0 .3.2.5.4.5h3v3c0 .2.2.4.5.4h1.4c.2 0 .4-.2.4-.4zm6.9 0v-3h3c.2 0 .4-.2.4-.5v-1.4c0-.2-.2-.4-.4-.4H16c-.4 0-.8.4-.8.8v4.5c0 .2.2.4.4.4h1.5c.3 0 .5-.2.5-.4z" fill="currentColor"/></svg>';

		// Invalidate map size (Leaflet needs this when container resizes)
		setTimeout(() => {
			this.map.invalidateSize();
		}, 100);

		this.isFullWindow = true;

	}

	exitFullWindow() {
		// Find the filter wrap
		const filterWrap = this.mapContainer.querySelector('.geodir-map-cat-filter-wrap');

		// Restore original styles for map container
		if (this.originalStyles.mapContainer) {
			Object.keys(this.originalStyles.mapContainer).forEach(prop => {
				this.mapContainer.style[prop] = this.originalStyles.mapContainer[prop];
			});
		}

		// Restore filter wrap display and maxHeight
		if (filterWrap && this.originalStyles.filterWrap) {
			filterWrap.style.display = this.originalStyles.filterWrap.display;
			filterWrap.style.maxHeight = this.originalStyles.filterWrap.maxHeight;
		}

		// Remove class
		this.mapContainer.classList.remove('geodir-map-fullwindow');

		// Move map container back to its original position in the DOM
		if (this.originalParent) {
			if (this.originalNextSibling) {
				// Insert before the next sibling (original position)
				this.originalParent.insertBefore(this.mapContainer, this.originalNextSibling);
			} else {
				// It was the last child, so append
				this.originalParent.appendChild(this.mapContainer);
			}
		}

		// Update button state
		this.button.classList.remove('active');
		this.button.title = 'Toggle Full Window';
		this.button.innerHTML = '<svg width="30" height="30" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg"><path d="M5 10.3V5.9c0-.5.4-.9.9-.9h4.4c.2 0 .4.2.4.4V7c0 .2-.2.4-.4.4h-3v3c0 .2-.2.4-.4.4H5.4a.4.4 0 0 1-.4-.4zm10.3-4.9V7c0 .2.2.4.4.4h3v3c0 .2.2.4.4.4h1.5c.2 0 .4-.2.4-.4V5.9c0-.5-.4-.9-.9-.9h-4.4c-.2 0-.4.2-.4.4zm5.3 9.9H19c-.2 0-.4.2-.4.4v3h-3c-.2 0-.4.2-.4.4v1.5c0 .2.2.4.4.4h4.4c.5 0 .9-.4.9-.9v-4.4c0-.2-.2-.4-.4-.4zm-9.9 5.3V19c0-.2-.2-.4-.4-.4h-3v-3c0-.2-.2-.4-.4-.4H5.4c-.2 0-.4.2-.4.4v4.4c0 .5.4.9.9.9h4.4c.2 0 .4-.2.4-.4z" fill="currentColor"/></svg>';

		// Invalidate map size
		setTimeout(() => {
			this.map.invalidateSize();
		}, 100);

		this.isFullWindow = false;

	}
}
